import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link as RouterLink, useNavigate, useSearchParams } from 'react-router-dom';
import {
  Box,
  Button,
  FormControl,
  FormLabel,
  Heading,
  HStack,
  Input,
  Select,
  Spinner,
  Text,
  VStack,
  useToast,
  Divider,
  Flex,
  Link,
  IconButton,
  InputGroup,
  InputRightElement,
  Icon,
} from '@chakra-ui/react';
import { FiRefreshCw, FiCopy, FiEye, FiEyeOff, FiX, FiCheck } from 'react-icons/fi';
import { openOAuthInNewTab } from '../utils/openOAuthInNewTab';
import {
  consumeOAuthFlowStarted,
  publishOAuthResult,
  subscribeOAuthResult,
} from '../utils/oauthCrossTab';
import OAuthTabCompleteCard from '../components/OAuthTabCompleteCard';
import TotpQrCode from '../components/TotpQrCode';
import EmailWarmingSection from '../components/emailInfra/EmailWarmingSection';
import OutboundPhoneSection from '../components/emailInfra/OutboundPhoneSection';
import { OUTBOUND_SETUP_PATH } from '../constants/outboundSetup';
import {
  assignMailboxes,
  checkDomainAvailability,
  createDomainOrder,
  fetchZapmailEntitlements,
  getOAuthAuthUrl,
  listDomains,
  listMailboxes,
  listPendingMailboxes,
  startDomainCheckout,
  startMailboxCheckout,
  syncMailboxes,
  zapmailDataPayload,
} from '../services/zapmailService';
import { applySeatCheckoutResponse } from '../utils/stripeSeatCheckout';
import {
  clearPendingMailboxAssign,
  loadPendingMailboxAssign,
  savePendingMailboxAssign,
} from '../utils/pendingMailboxAssign';
import { formatUsdFromCents, formatUsdFromDollars } from '../utils/formatCurrency';
import {
  alertBannerProps,
  bodyTextProps,
  contentColumnProps,
  dividerProps,
  formLabelProps,
  inputFieldProps,
  listContainerProps,
  listRowProps,
  mutedTextProps,
  outlineButtonProps,
  pageShellProps,
  pageTitleProps,
  pageSubtitleProps,
  panelBoxProps,
  primaryButtonProps,
  rowTitleTextProps,
  sectionTitleProps,
} from './emailInfraPageTheme';

const RETURN_PATH = OUTBOUND_SETUP_PATH;

const availabilityDomainName = (row) =>
  (row.domain || row.domainName || row.name || '').trim().toLowerCase();

const formatAvailabilityPriceLabel = (row, freeDomainAvailable, ownedFqdns) => {
  const name = availabilityDomainName(row);
  if (name && ownedFqdns?.has(name)) {
    return 'Claimed';
  }
  const st = (row.status || '').toUpperCase();
  if (st !== 'AVAILABLE') {
    return st || '—';
  }
  if (freeDomainAvailable) {
    return 'Free';
  }
  let cents = row.gr_amount_cents;
  if (cents == null || cents === '') {
    const raw = row.domainPrice ?? row.domain_price;
    const n = parseFloat(String(raw || '').replace(/[^\d.]/g, ''), 10);
    if (!Number.isNaN(n)) {
      cents = Math.round(n * 1.5 * 100);
    }
  }
  if (cents != null && cents !== '') {
    return `${formatUsdFromCents(cents)}/yr`;
  }
  if (row.gr_list_price_usd != null && row.gr_list_price_usd !== '') {
    return `${formatUsdFromDollars(row.gr_list_price_usd)}/yr`;
  }
  return '—';
};

const MAILBOX_PROVIDERS = [
  { id: 'gmail', label: 'Google Workspace (Gmail)' },
  { id: 'outlook', label: 'Microsoft (Outlook)' },
];

const ZAPMAIL_UUID_RE =
  /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

const SectionSpinner = ({ minHeight = '4.5rem' }) => (
  <Flex align="center" justify="center" py={6} minH={minHeight} w="full">
    <Spinner size="md" color="blue.400" thickness="3px" speed="0.65s" />
  </Flex>
);

const mailboxDisplayEmail = (row) => {
  const e = (row.email || '').trim();
  if (e && e.includes('@')) return e;
  const user = (row.username || '').trim();
  const dom = (row.fqdn || '').trim();
  if (user && dom) return `${user}@${dom}`;
  return user || '';
};

const MailboxZapmailCredentialFields = ({ row, toast }) => {
  const accountEmail = mailboxDisplayEmail(row);
  const hasAny =
    row.zapmail_login_password || row.zapmail_app_password || row.zapmail_totp_secret;
  if (!hasAny) {
    return (
      <Text {...mutedTextProps} mt={3}>
        Password and 2FA details are not available yet. They usually appear once the mailbox is ACTIVE—use
        Sync mailboxes, then check again.
      </Text>
    );
  }
  return (
    <VStack align="stretch" spacing={0} mt={3}>
      {row.zapmail_login_password && (
        <CredentialRow
          label="Mailbox password (for Google / Microsoft sign-in)"
          value={row.zapmail_login_password}
          toast={toast}
        />
      )}
      {row.zapmail_app_password && (
        <CredentialRow
          label="App password (if Google asks for an app password)"
          value={row.zapmail_app_password}
          toast={toast}
        />
      )}
      {row.zapmail_totp_secret && (
        <>
          <TotpQrCode secret={row.zapmail_totp_secret} accountEmail={accountEmail} />
          <CredentialRow
            label="2FA secret (manual entry)"
            value={row.zapmail_totp_secret}
            toast={toast}
          />
          <Text fontSize="xs" color="rgba(255,255,255,0.4)" mt={1} mb={2}>
            When Google or Microsoft asks for a verification code, enter the 6-digit code from your
            authenticator app—not this secret string.
          </Text>
        </>
      )}
    </VStack>
  );
};

const CredentialRow = ({ label, value, toast }) => {
  const [visible, setVisible] = useState(false);
  if (!value) return null;
  const copy = async () => {
    try {
      await navigator.clipboard.writeText(value);
      toast({ title: `${label} copied`, status: 'success', duration: 2000, isClosable: true });
    } catch {
      toast({ title: 'Could not copy', status: 'error', duration: 2000, isClosable: true });
    }
  };
  return (
    <FormControl size="sm" mt={2}>
      <FormLabel mb={1} {...formLabelProps} fontSize="xs">
        {label}
      </FormLabel>
      <InputGroup size="sm">
        <Input
          readOnly
          type={visible ? 'text' : 'password'}
          value={value}
          fontFamily="mono"
          fontSize="sm"
          {...inputFieldProps}
        />
        <InputRightElement width="4.5rem">
          <HStack spacing={0}>
            <IconButton
              aria-label={visible ? 'Hide' : 'Show'}
              size="xs"
              variant="ghost"
              icon={visible ? <FiEyeOff /> : <FiEye />}
              onClick={() => setVisible((v) => !v)}
            />
            <IconButton aria-label="Copy" size="xs" variant="ghost" icon={<FiCopy />} onClick={copy} />
          </HStack>
        </InputRightElement>
      </InputGroup>
    </FormControl>
  );
};

const EmailDomainsPage = () => {
  const toast = useToast();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  const [loading, setLoading] = useState(true);
  const [entitlements, setEntitlements] = useState(null);
  const [provider, setProvider] = useState('gmail');
  const [searchInput, setSearchInput] = useState('');
  const [searching, setSearching] = useState(false);
  const [availability, setAvailability] = useState([]);
  const [domains, setDomains] = useState([]);
  const [mailboxes, setMailboxes] = useState([]);
  const [pending, setPending] = useState([]);
  const [actionLoading, setActionLoading] = useState(false);
  const [syncingMailboxes, setSyncingMailboxes] = useState(false);
  const [oauthReturnUi, setOauthReturnUi] = useState(null);

  const [assignDomainId, setAssignDomainId] = useState('');
  const [assignLocalPart, setAssignLocalPart] = useState('');
  const [assignFirstName, setAssignFirstName] = useState('');
  const [assignLastName, setAssignLastName] = useState('');

  const paidOk = entitlements?.can_use_email_infra;
  const domainsListLoading = loading || syncingMailboxes;
  const oauthListLoading = loading || syncingMailboxes;

  const ownedDomainFqdns = useMemo(() => {
    const set = new Set();
    for (const d of domains) {
      const fq = (d.fqdn || '').trim().toLowerCase();
      if (fq) set.add(fq);
    }
    return set;
  }, [domains]);

  const pendingOAuthMailboxes = useMemo(() => {
    const label = (row) =>
      (
        row.email ||
        (row.username && row.fqdn ? `${row.username}@${row.fqdn}` : '') ||
        row.fqdn ||
        ''
      ).toLowerCase();
    return [...pending].sort((a, b) => label(a).localeCompare(label(b)));
  }, [pending]);

  const refreshAll = useCallback(async () => {
    setLoading(true);
    try {
      const ent = await fetchZapmailEntitlements();
      setEntitlements(ent);
      if (!ent.can_use_email_infra) {
        setDomains([]);
        setMailboxes([]);
        setPending([]);
        return;
      }
      try {
        await syncMailboxes('gmail');
      } catch {
        /* sync is best-effort before listing */
      }
      try {
        await syncMailboxes('outlook');
      } catch {
        /* sync is best-effort before listing */
      }
      const [domRes, mbRes, pendRes] = await Promise.all([
        listDomains(provider),
        listMailboxes(provider),
        listPendingMailboxes(),
      ]);
      setDomains(domRes.domains || []);
      setMailboxes(mbRes.mailboxes || []);
      setPending(pendRes.pending || []);
    } catch (e) {
      toast({
        title: 'Could not load email infrastructure',
        description: e.response?.data?.detail || e.message,
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setLoading(false);
    }
  }, [provider, toast]);

  const handleSyncMailboxes = useCallback(async () => {
    if (!paidOk) return;
    setSyncingMailboxes(true);
    try {
      try {
        await syncMailboxes('gmail');
      } catch {
        /* best-effort */
      }
      try {
        await syncMailboxes('outlook');
      } catch {
        /* best-effort */
      }
      const [domRes, mbRes, pendRes] = await Promise.all([
        listDomains(provider),
        listMailboxes(provider),
        listPendingMailboxes(),
      ]);
      setDomains(domRes.domains || []);
      setMailboxes(mbRes.mailboxes || []);
      setPending(pendRes.pending || []);
    } catch (e) {
      toast({
        title: 'Could not sync mailboxes',
        description: e.response?.data?.detail || e.message,
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setSyncingMailboxes(false);
    }
  }, [paidOk, provider, toast]);

  useEffect(() => {
    refreshAll();
  }, [refreshAll]);

  const mailboxCheckoutHandledRef = useRef(false);

  const domainOptions = useMemo(() => {
    const map = new Map();
    const add = (id, fqdn, zapmailDomainId = null) => {
      const name = (fqdn || '').trim().toLowerCase();
      if (!name) return;
      const zid =
        zapmailDomainId && ZAPMAIL_UUID_RE.test(String(zapmailDomainId))
          ? String(zapmailDomainId)
          : null;
      const optionId = zid || String(id || name);
      map.set(optionId, {
        id: optionId,
        fqdn: name,
        zapmailDomainId: zid,
      });
    };
    for (const d of domains) {
      add(d.zapmail_domain_id || d._id, d.fqdn, d.zapmail_domain_id);
    }
    for (const m of mailboxes) {
      if (m.fqdn && m.zapmail_domain_id && ZAPMAIL_UUID_RE.test(String(m.zapmail_domain_id))) {
        add(m.zapmail_domain_id, m.fqdn, m.zapmail_domain_id);
      }
    }
    return [...map.values()].sort((a, b) => a.fqdn.localeCompare(b.fqdn));
  }, [domains, mailboxes]);

  const assignReadyDomainOptions = useMemo(
    () => domainOptions.filter((d) => d.zapmailDomainId),
    [domainOptions]
  );

  const selectedDomain = useMemo(
    () => domainOptions.find((d) => String(d.id) === String(assignDomainId)),
    [domainOptions, assignDomainId]
  );

  useEffect(() => {
    if (!domainOptions.length) {
      if (assignDomainId) setAssignDomainId('');
      return;
    }
    const exists = domainOptions.some((d) => String(d.id) === String(assignDomainId));
    if (!exists) {
      setAssignDomainId(String(domainOptions[0].id));
    }
  }, [domainOptions, assignDomainId]);

  const handleSearch = async () => {
    const names = searchInput
      .split(/[\s,]+/)
      .map((s) => s.trim().toLowerCase())
      .filter(Boolean);
    if (!names.length) return;
    setSearching(true);
    try {
      const raw = await checkDomainAvailability(names, provider);
      const data = zapmailDataPayload(raw);
      setAvailability(data.domains || []);
    } catch (e) {
      toast({
        title: 'Availability check failed',
        description: e.response?.data?.detail || e.message,
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setSearching(false);
    }
  };

  const clearDomainSearch = () => {
    setSearchInput('');
    setAvailability([]);
  };

  const handlePurchase = async (fqdn) => {
    setActionLoading(true);
    try {
      const order = await createDomainOrder(fqdn, provider, 1);
      if (order.requires_checkout) {
        const origin = window.location.origin;
        const { url } = await startDomainCheckout(
          order.order_id,
          `${origin}${RETURN_PATH}?checkout=domain`,
          `${origin}${RETURN_PATH}?checkout=cancel`
        );
        window.location.href = url;
        return;
      }
      toast({
        title: order.complimentary ? 'Complimentary domain registered' : 'Domain registered',
        status: 'success',
        duration: 5000,
        isClosable: true,
      });
      await refreshAll();
    } catch (e) {
      toast({
        title: 'Domain order failed',
        description: e.response?.data?.detail || e.message,
        status: 'error',
        duration: 6000,
        isClosable: true,
      });
    } finally {
      setActionLoading(false);
    }
  };

  const runAssignInbox = useCallback(
    async (pending = null) => {
      const prov = pending?.provider || provider;
      const local = (pending?.mailboxUsername ?? assignLocalPart).trim();
      const fqdn = pending?.fqdn || selectedDomain?.fqdn;
      const domainIdForApi =
        pending?.domainZapmailId ||
        selectedDomain?.zapmailDomainId ||
        fqdn;
      if (!fqdn || !local) {
        toast({
          title: 'Enter an inbox username and domain',
          status: 'warning',
          duration: 3000,
          isClosable: true,
        });
        return false;
      }
      const firstName =
        (pending?.firstName ?? assignFirstName).trim() || local;
      const lastName = (pending?.lastName ?? assignLastName).trim() || 'User';
      const mailboxPayload = {
        domainName: fqdn,
        mailboxUsername: local,
        firstName,
        lastName,
      };
      await assignMailboxes(prov, domainIdForApi, [mailboxPayload]);
      await syncMailboxes(prov);
      toast({
        title: 'Mailbox provisioning started',
        description:
          'When the inbox is ACTIVE, sync mailboxes, then use the mailbox password below when you connect OAuth.',
        status: 'success',
        duration: 7000,
        isClosable: true,
      });
      setAssignLocalPart('');
      clearPendingMailboxAssign();
      await refreshAll();
      return true;
    },
    [
      assignFirstName,
      assignLastName,
      assignLocalPart,
      provider,
      refreshAll,
      selectedDomain,
      toast,
    ]
  );

  const completePendingAssignAfterCheckout = useCallback(async () => {
    const pending = loadPendingMailboxAssign();
    if (!pending) return false;

    if (pending.provider && pending.provider !== provider) {
      setProvider(pending.provider);
    }

    const maxAttempts = 10;
    const delayMs = 1500;
    let lastErr = null;
    for (let attempt = 0; attempt < maxAttempts; attempt += 1) {
      try {
        const ent = await fetchZapmailEntitlements();
        setEntitlements(ent);
        const remaining = ent?.mailbox_seats_remaining ?? 0;
        if (remaining <= 0 && attempt < maxAttempts - 1) {
          await new Promise((r) => setTimeout(r, delayMs));
          continue;
        }
        await runAssignInbox(pending);
        return true;
      } catch (e) {
        lastErr = e;
        const status = e.response?.status;
        if (status === 402 && attempt < maxAttempts - 1) {
          await new Promise((r) => setTimeout(r, delayMs));
          continue;
        }
        break;
      }
    }
    toast({
      title: 'Could not create inbox after payment',
      description:
        lastErr?.response?.data?.detail ||
        lastErr?.message ||
        'Your seat was added—click Buy mailbox again or contact support.',
      status: 'error',
      duration: 8000,
      isClosable: true,
    });
    return false;
  }, [provider, runAssignInbox, toast]);

  useEffect(() => {
    const mailboxCheckout = searchParams.get('mailbox_checkout');
    if (mailboxCheckout === 'cancel') {
      clearPendingMailboxAssign();
      setSearchParams({}, { replace: true });
      return;
    }

    const checkout = searchParams.get('checkout');
    const gmailOk = searchParams.get('gmail_connected') === 'true';
    const gmailErr = searchParams.get('gmail_error') === 'true';
    const outlookOk = searchParams.get('outlook_connected') === 'true';
    const outlookErr = searchParams.get('outlook_error') === 'true';

    if (mailboxCheckout === 'success' && !mailboxCheckoutHandledRef.current) {
      mailboxCheckoutHandledRef.current = true;
      setSearchParams({}, { replace: true });
      (async () => {
        setActionLoading(true);
        toast({
          title: 'Payment received',
          description: 'Creating your inbox in ZapMail…',
          status: 'success',
          duration: 5000,
          isClosable: true,
        });
        const hadPending = Boolean(loadPendingMailboxAssign());
        if (hadPending) {
          await completePendingAssignAfterCheckout();
        } else {
          toast({
            title: 'Mailbox subscription active',
            description: 'Enter inbox details and click Buy mailbox to provision.',
            status: 'info',
            duration: 6000,
            isClosable: true,
          });
          await refreshAll();
        }
        setActionLoading(false);
      })();
      return;
    }

    if (gmailOk || gmailErr || outlookOk || outlookErr) {
      const oauthProvider = gmailOk || gmailErr ? 'gmail' : 'outlook';
      const oauthOk = gmailOk || outlookOk;
      const flow = consumeOAuthFlowStarted();
      publishOAuthResult({ provider: oauthProvider, ok: oauthOk });
      if (flow) {
        setOauthReturnUi({
          provider: oauthProvider,
          ok: oauthOk,
          accountEmail: flow.accountEmail,
        });
      } else if (oauthOk) {
        toast({ title: 'Mailbox connected', status: 'success', duration: 4000, isClosable: true });
      } else {
        toast({
          title: 'Mailbox connection failed',
          status: 'error',
          duration: 5000,
          isClosable: true,
        });
      }
      setSearchParams({}, { replace: true });
      refreshAll();
      return;
    }

    if (checkout) {
      if (checkout === 'domain') {
        toast({ title: 'Domain payment received — finishing setup…', status: 'success', duration: 5000, isClosable: true });
      }
      if (checkout === 'mailbox') {
        toast({ title: 'Mailbox subscription active', status: 'success', duration: 5000, isClosable: true });
      }
      setSearchParams({}, { replace: true });
      refreshAll();
    }
  }, [
    searchParams,
    setSearchParams,
    toast,
    refreshAll,
    completePendingAssignAfterCheckout,
  ]);

  useEffect(() => {
    return subscribeOAuthResult(({ provider, ok }) => {
      const label = provider === 'gmail' ? 'Google' : 'Microsoft';
      toast({
        title: ok ? `${label} mailbox connected` : `${label} connection failed`,
        description: ok
          ? 'You can continue on this tab—the other sign-in tab may be closed.'
          : 'Try Connect OAuth again when ready.',
        status: ok ? 'success' : 'error',
        duration: 6000,
        isClosable: true,
      });
      refreshAll();
    });
  }, [toast, refreshAll]);

  useEffect(() => {
    if (window.location.hash !== '#email-warming') return;
    const el = document.getElementById('email-warming');
    if (el) {
      requestAnimationFrame(() => el.scrollIntoView({ behavior: 'smooth', block: 'start' }));
    }
  }, [loading]);

  const handleAssign = async () => {
    setActionLoading(true);
    try {
      await runAssignInbox();
    } catch (e) {
      const status = e.response?.status;
      const detail = e.response?.data?.detail || e.message;
      if (status === 402) {
        const local = assignLocalPart.trim();
        const fqdn = selectedDomain?.fqdn;
        if (!fqdn || !local) {
          toast({
            title: 'Mailbox limit reached',
            description: detail,
            status: 'warning',
            duration: 6000,
            isClosable: true,
          });
          return;
        }
        const domainIdForApi = selectedDomain.zapmailDomainId || fqdn;
        savePendingMailboxAssign({
          provider,
          fqdn,
          domainZapmailId: domainIdForApi,
          mailboxUsername: local,
          firstName: assignFirstName.trim() || local,
          lastName: assignLastName.trim() || 'User',
        });
        toast({
          title: 'Mailbox limit reached',
          description: `${detail} Adding a paid seat…`,
          status: 'warning',
          duration: 5000,
          isClosable: true,
        });
        try {
          const origin = window.location.origin;
          const data = await startMailboxCheckout(
            provider,
            1,
            `${origin}${OUTBOUND_SETUP_PATH}?mailbox_checkout=success`,
            `${origin}${OUTBOUND_SETUP_PATH}?mailbox_checkout=cancel`
          );
          if (data?.status === 'quantity_updated') {
            await applySeatCheckoutResponse(data, {
              toast,
              onUpdated: refreshAll,
              redirect: false,
            });
            try {
              await runAssignInbox();
            } catch (retryErr) {
              toast({
                title: 'Seat added — finish creating the inbox',
                description:
                  retryErr.response?.data?.detail || retryErr.message || 'Try Create inbox again.',
                status: 'warning',
                duration: 7000,
                isClosable: true,
              });
            }
          } else {
            await applySeatCheckoutResponse(data, {
              toast,
              onUpdated: refreshAll,
              redirect: true,
            });
          }
        } catch (checkoutErr) {
          toast({
            title: 'Could not add mailbox seat',
            description: checkoutErr.response?.data?.detail || checkoutErr.message,
            status: 'error',
            duration: 6000,
            isClosable: true,
          });
        }
      } else {
        toast({ title: 'Assign failed', description: detail, status: 'error', duration: 6000, isClosable: true });
      }
    } finally {
      setActionLoading(false);
    }
  };

  const mailboxLoginHint = (row) => {
    const e = (row.email || '').trim().toLowerCase();
    if (e && e.includes('@')) return e;
    const user = (row.username || '').trim().toLowerCase();
    const dom = (row.fqdn || '').trim().toLowerCase();
    if (user && dom) return `${user}@${dom}`;
    return '';
  };

  const normalizedMailboxStatus = (row) => (row.zapmail_status || '').toUpperCase();

  const handleConnect = async (row) => {
    const prov = row.provider || provider;
    const st = normalizedMailboxStatus(row);
    if (st !== 'ACTIVE') {
      toast({
        title: 'Mailbox not ready for OAuth',
        description:
          'Connect OAuth is only available when ZapMail shows ACTIVE. Use Sync mailboxes until the status updates.',
        status: 'warning',
        duration: 9000,
        isClosable: true,
      });
      return;
    }
    try {
      const params = {
        zapmail_mailbox_id: row.zapmail_mailbox_id,
        zapmail_domain_id: row.zapmail_domain_id,
        zapmail_workspace_id: row.workspace_id || row.zapmail_workspace_id,
        return_path: RETURN_PATH,
      };
      if (prov === 'outlook') {
        const hint = mailboxLoginHint(row);
        if (hint) params.login_hint = hint;
      }
      const authUrl = await getOAuthAuthUrl(prov, params);
      openOAuthInNewTab(authUrl, toast, {
        provider: prov,
        accountEmail: mailboxLoginHint(row),
      });
    } catch (e) {
      toast({
        title: 'OAuth failed',
        description: e.response?.data?.detail || e.message,
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    }
  };

  if (loading && !entitlements) {
    return (
      <Box p={8} textAlign="center">
        <Spinner size="xl" color="blue.400" />
        <Text mt={4} color="gray.400">
          Loading outbound setup…
        </Text>
      </Box>
    );
  }

  return (
    <Box {...pageShellProps}>
      {oauthReturnUi && (
        <OAuthTabCompleteCard
          provider={oauthReturnUi.provider}
          ok={oauthReturnUi.ok}
          accountEmail={oauthReturnUi.accountEmail}
          onDismiss={() => setOauthReturnUi(null)}
        />
      )}
      <VStack align="stretch" spacing={8} {...contentColumnProps}>
        <Box>
          <Heading {...pageTitleProps} mb={1}>
            Outbound setup
          </Heading>
          <Text {...pageSubtitleProps}>
            Domains and inboxes, email warming, and a US outbound phone line—one flow for outbound
            email and calls.
          </Text>
        </Box>

        {!paidOk && (
          <Box {...alertBannerProps}>
            <Text fontWeight="medium" color="rgba(255,255,255,0.88)" mb={2}>
              Paid subscription required
            </Text>
            <Text {...bodyTextProps} mb={3}>
              Domains, mailboxes, and warming are available on Starter, Basic, and Pro plans.
            </Text>
            <Button {...primaryButtonProps} onClick={() => navigate('/billing')}>
              View plans
            </Button>
          </Box>
        )}

        <Box>
          <Text {...sectionTitleProps} mb={3}>
            Mailbox provider
          </Text>
          <Box
            {...listContainerProps}
            opacity={paidOk ? 1 : 0.55}
            pointerEvents={paidOk ? 'auto' : 'none'}
          >
            {MAILBOX_PROVIDERS.map((ws, i) => {
              const selected = provider === ws.id;
              const bundleMo = formatUsdFromCents(
                entitlements?.provisioned_inbox_monthly_gr_cents || 3300
              );
              return (
                <HStack
                  key={ws.id}
                  {...listRowProps(i === MAILBOX_PROVIDERS.length - 1)}
                  spacing={4}
                  cursor={paidOk ? 'pointer' : 'not-allowed'}
                  onClick={() => paidOk && setProvider(ws.id)}
                  bg={selected ? 'rgba(255,255,255,0.08)' : 'transparent'}
                  boxShadow={
                    selected ? 'inset 0 0 0 1px rgba(255,255,255,0.28)' : 'none'
                  }
                  _hover={
                    paidOk
                      ? {
                          bg: selected ? 'rgba(255,255,255,0.09)' : 'rgba(255,255,255,0.025)',
                        }
                      : undefined
                  }
                  role="radio"
                  aria-checked={selected}
                  tabIndex={paidOk ? 0 : -1}
                  onKeyDown={(e) => {
                    if (paidOk && (e.key === 'Enter' || e.key === ' ')) {
                      e.preventDefault();
                      setProvider(ws.id);
                    }
                  }}
                >
                  <Box
                    flexShrink={0}
                    w="20px"
                    h="20px"
                    borderRadius="full"
                    border="2px solid"
                    borderColor={selected ? '#fafafa' : 'rgba(255,255,255,0.22)'}
                    bg={selected ? '#fafafa' : 'transparent'}
                    display="flex"
                    alignItems="center"
                    justifyContent="center"
                    transition="background 0.12s ease, border-color 0.12s ease"
                  >
                    {selected && <Icon as={FiCheck} boxSize={3} color="#0a0a0a" strokeWidth={3} />}
                  </Box>
                  <Box flex={1} minW={0}>
                    <Text
                      {...rowTitleTextProps}
                      color={selected ? '#fafafa' : 'rgba(255,255,255,0.72)'}
                    >
                      {ws.label}
                    </Text>
                  </Box>
                  <Box flexShrink={0} textAlign="right">
                    <Text
                      fontSize="xs"
                      color={selected ? 'rgba(255,255,255,0.65)' : 'rgba(255,255,255,0.45)'}
                      letterSpacing="0.02em"
                    >
                      {bundleMo}/mo
                    </Text>
                    <Text fontSize="10px" color="rgba(255,255,255,0.28)">
                      per inbox · warming included
                    </Text>
                  </Box>
                </HStack>
              );
            })}
          </Box>
        </Box>

        {paidOk && (
          <>
            <Box>
              <Text {...sectionTitleProps} mb={3}>
                Find a domain
              </Text>
              <HStack mb={3}>
                <InputGroup flex={1}>
                  <Input
                    placeholder="example.com or comma-separated names"
                    value={searchInput}
                    onChange={(e) => setSearchInput(e.target.value)}
                    pr={searchInput.trim() || availability.length > 0 ? '2.5rem' : undefined}
                    onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
                    {...inputFieldProps}
                  />
                  {(searchInput.trim() || availability.length > 0) && (
                    <InputRightElement width="2.5rem">
                      <IconButton
                        h="1.75rem"
                        size="sm"
                        variant="ghost"
                        color="rgba(255,255,255,0.35)"
                        _hover={{ color: '#fafafa', bg: 'transparent' }}
                        onClick={clearDomainSearch}
                        icon={<FiX />}
                        aria-label="Clear search"
                      />
                    </InputRightElement>
                  )}
                </InputGroup>
                <Button {...primaryButtonProps} onClick={handleSearch} isLoading={searching}>
                  Search
                </Button>
              </HStack>
              {availability.length > 0 ? (
                <Box {...listContainerProps}>
                  {availability.map((row, i) => {
                    const name = row.domain || row.domainName || row.name;
                    const st = (row.status || '').toUpperCase();
                    const nameKey = (name || '').trim().toLowerCase();
                    const isClaimed = ownedDomainFqdns.has(nameKey);
                    const priceLabel = formatAvailabilityPriceLabel(
                      row,
                      entitlements?.free_domain_available,
                      ownedDomainFqdns
                    );
                    const isLast = i === availability.length - 1;
                    return (
                      <HStack key={name} justify="space-between" {...listRowProps(isLast)}>
                        <Text {...rowTitleTextProps}>{name}</Text>
                        <HStack>
                          <Text
                            fontSize="xs"
                            color={
                              isClaimed
                                ? 'rgba(34,197,94,0.85)'
                                : 'rgba(255,255,255,0.45)'
                            }
                            letterSpacing="0.02em"
                          >
                            {priceLabel}
                          </Text>
                          {st === 'AVAILABLE' && !isClaimed && (
                            <Button
                              {...primaryButtonProps}
                              onClick={() => handlePurchase(name)}
                              isLoading={actionLoading}
                            >
                              {entitlements?.free_domain_available ? 'Register (free)' : 'Buy'}
                            </Button>
                          )}
                        </HStack>
                      </HStack>
                    );
                  })}
                </Box>
              ) : (
                <Text {...mutedTextProps}>Search to see available domains.</Text>
              )}
            </Box>

            <Divider {...dividerProps} />

            <Box>
              <Text {...sectionTitleProps} mb={3}>
                Your domains
              </Text>
              {domainsListLoading ? (
                <SectionSpinner minHeight="5rem" />
              ) : domains.length === 0 ? (
                <Text {...mutedTextProps}>No domains yet.</Text>
              ) : (
                <Box {...listContainerProps}>
                  {domains.map((d, i) => (
                    <Box key={d._id} {...listRowProps(i === domains.length - 1)}>
                      <Text {...rowTitleTextProps}>{d.fqdn}</Text>
                      <Text fontSize="xs" color="rgba(255,255,255,0.28)" mt={0.5}>
                        Status: {d.status || '—'}
                        {d.zapmail_domain_id ? ' · Ready for inbox' : ' · Activating in ZapMail'}
                      </Text>
                    </Box>
                  ))}
                </Box>
              )}
            </Box>

            <Box {...panelBoxProps}>
              <Text {...sectionTitleProps} mb={3}>
                Provision inbox
              </Text>
              {domainOptions.length === 0 ? (
                <Text {...mutedTextProps} mb={3}>
                  No domains for {provider === 'outlook' ? 'Outlook' : 'Gmail'} yet. Buy the domain
                  with the same provider selected above, wait for activation, then Sync mailboxes.
                </Text>
              ) : assignReadyDomainOptions.length === 0 ? (
                <Text {...mutedTextProps} mb={3}>
                  Your domain is still activating in ZapMail (DNS / connection). This often takes a
                  few minutes up to an hour. Keep {provider === 'outlook' ? 'Outlook' : 'Gmail'}{' '}
                  selected, click Sync mailboxes, and retry. Domains bought under the other provider
                  never appear here.
                </Text>
              ) : (
                <Text {...mutedTextProps} mb={3}>
                  Domains are tied to the selected provider. A domain bought under Gmail is not
                  available for Outlook inboxes (and vice versa).
                </Text>
              )}
              <VStack align="stretch" spacing={3}>
                <FormControl>
                  <FormLabel {...formLabelProps}>Inbox email address</FormLabel>
                  <HStack align="center" spacing={2}>
                    <Input
                      flex={1}
                      placeholder="your name"
                      value={assignLocalPart}
                      onChange={(e) => setAssignLocalPart(e.target.value.replace(/\s/g, ''))}
                      aria-label="Inbox username"
                      {...inputFieldProps}
                    />
                    <Text color="rgba(255,255,255,0.35)" fontWeight="medium" userSelect="none">
                      @
                    </Text>
                    <Select
                      flex={1.2}
                      value={assignDomainId}
                      onChange={(e) => setAssignDomainId(e.target.value)}
                      aria-label="Domain"
                      isDisabled={domainOptions.length === 0}
                      {...inputFieldProps}
                    >
                      {domainOptions.map((d) => {
                        const claimed = ownedDomainFqdns.has((d.fqdn || '').toLowerCase());
                        let suffix = '';
                        if (!d.zapmailDomainId) suffix = ' (activating…)';
                        else if (claimed) suffix = ' (claimed)';
                        return (
                          <option key={d.id} value={d.id}>
                            {d.fqdn}
                            {suffix}
                          </option>
                        );
                      })}
                    </Select>
                  </HStack>
                  <Text fontSize="xs" color="rgba(255,255,255,0.28)" mt={2}>
                    {selectedDomain?.fqdn && assignLocalPart.trim()
                      ? `Creates ${assignLocalPart.trim()}@${selectedDomain.fqdn}`
                      : 'Choose the username and domain for this sending inbox.'}
                  </Text>
                </FormControl>
                <HStack align="flex-start">
                  <FormControl>
                    <FormLabel {...formLabelProps}>First name</FormLabel>
                    <Input
                      value={assignFirstName}
                      onChange={(e) => setAssignFirstName(e.target.value)}
                      {...inputFieldProps}
                    />
                  </FormControl>
                  <FormControl>
                    <FormLabel {...formLabelProps}>Last name</FormLabel>
                    <Input
                      value={assignLastName}
                      onChange={(e) => setAssignLastName(e.target.value)}
                      {...inputFieldProps}
                    />
                  </FormControl>
                </HStack>
                <Button {...primaryButtonProps} alignSelf="flex-start" onClick={handleAssign} isLoading={actionLoading}>
                  Buy mailbox
                </Button>
              </VStack>
            </Box>

            <HStack justify="flex-end" mb={4}>
              <Button
                leftIcon={<FiRefreshCw />}
                {...outlineButtonProps}
                onClick={handleSyncMailboxes}
                isLoading={syncingMailboxes}
                loadingText="Syncing…"
                isDisabled={syncingMailboxes}
              >
                Sync mailboxes
              </Button>
            </HStack>

            <Box>
              <Text {...sectionTitleProps} mb={2}>
                Connect OAuth
                {pendingOAuthMailboxes.length > 0
                  ? ` (${pendingOAuthMailboxes.length} inbox${pendingOAuthMailboxes.length === 1 ? '' : 'es'})`
                  : ''}
              </Text>
              <Text {...bodyTextProps} mb={3}>
                Click <strong>Connect OAuth</strong> to open Google or Microsoft sign-in in a new tab. Keep this
                page open to copy the mailbox email, password, app password, or 2FA code below—not your
                GillyReach login. Warming starts automatically after OAuth succeeds in that tab.
              </Text>
              <Text {...mutedTextProps} mb={3} fontSize="xs">
                <strong>Connect OAuth</strong> is enabled only when ZapMail status is <strong>ACTIVE</strong>.
                Use <strong>Sync mailboxes</strong> until provisioning finishes (IN_PROGRESS → ACTIVE).
              </Text>
              {oauthListLoading ? (
                <SectionSpinner minHeight="8rem" />
              ) : pendingOAuthMailboxes.length === 0 ? (
                <Text {...mutedTextProps}>No mailboxes awaiting OAuth.</Text>
              ) : (
                <VStack align="stretch" spacing={3}>
                  {pendingOAuthMailboxes.map((row) => {
                    const email =
                      row.email ||
                      (row.username && row.fqdn
                        ? `${row.username}@${row.fqdn}`
                        : row.username
                          ? `${row.username}@…`
                          : 'Mailbox');
                    const statusRaw = row.zapmail_status || 'unknown';
                    const status = statusRaw.toUpperCase();
                    const isActive = status === 'ACTIVE';
                    const oauthConnectDisabled = !isActive;
                    return (
                      <Box key={row._id} {...panelBoxProps}>
                        <HStack justify="space-between" align="flex-start" flexWrap="wrap" gap={2}>
                          <Box flex={1} minW="200px">
                            <Text {...rowTitleTextProps}>{email}</Text>
                            <HStack mt={1.5} spacing={2} flexWrap="wrap">
                              <Text fontSize="xs" color="rgba(255,255,255,0.35)">
                                {(row.provider || provider).toUpperCase()}
                              </Text>
                              <Box
                                px={1.5}
                                py="1px"
                                borderRadius="full"
                                bg={isActive ? 'rgba(34,197,94,0.1)' : 'rgba(255,180,80,0.08)'}
                                border={`1px solid ${isActive ? 'rgba(34,197,94,0.3)' : 'rgba(255,180,80,0.25)'}`}
                              >
                                <Text
                                  fontSize="9px"
                                  color={isActive ? '#4ade80' : 'rgba(255,180,80,0.9)'}
                                  fontWeight="600"
                                  letterSpacing="0.06em"
                                  textTransform="uppercase"
                                >
                                  {statusRaw}
                                </Text>
                              </Box>
                            </HStack>
                            {oauthConnectDisabled && (
                              <Text fontSize="xs" color="rgba(255,180,80,0.85)" mt={2}>
                                Sync mailboxes until status is ACTIVE, then use Connect OAuth.
                              </Text>
                            )}
                          </Box>
                          <Button
                            {...primaryButtonProps}
                            onClick={() => handleConnect(row)}
                            isDisabled={oauthConnectDisabled}
                          >
                            Connect OAuth
                          </Button>
                        </HStack>
                        <MailboxZapmailCredentialFields row={row} toast={toast} />
                      </Box>
                    );
                  })}
                </VStack>
              )}
            </Box>

            <Divider {...dividerProps} />

            <EmailWarmingSection parentPaidOk={paidOk} />

            <Divider {...dividerProps} />

            <OutboundPhoneSection parentPaidOk={paidOk} />
          </>
        )}
      </VStack>
    </Box>
  );
};

export default EmailDomainsPage;
